import React, { useMemo, useRef } from 'react';
import { GoalContextMenu } from 'shared/components/connected/goal-context-menu';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Tooltip } from 'shared/components/ui/tooltip';
import { tooltipDelay } from 'shared/constants';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { getCompletionPercentage } from 'shared/utils/get-completion-percentage';
import { getGoalCompletion } from 'shared/utils/get-goal-completion';
import { getLinkedItemColor } from 'shared/utils/get-linked-item-color';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { useTimeBasedStyle } from './use-time-based-style';

export type GoalEventProps = {
  goalId: ID;
  startDate?: string;
  endDate?: string;
  goals: Goal[];
  lifeAreas: LifeArea[];
  onClick?: (id: ID) => void;
  isDeadline?: boolean;
  isPast?: boolean;
};

export const GoalEvent: React.FC<GoalEventProps> = ({
  goalId,
  startDate,
  endDate,
  goals,
  lifeAreas,
  onClick,
  isDeadline,
  isPast,
}) => {
  const goal = useMemo(
    () => goals.find(({ id }) => id === goalId),
    [goals, goalId],
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const containerRef = useRef<HTMLDivElement>(null);

  const { numberOfLines } = useTimeBasedStyle(startDate, endDate);

  const completionPercentage = useMemo(
    () =>
      goal
        ? getCompletionPercentage(getGoalCompletion(goal, { goals }))
        : undefined,
    [goal, goals],
  );

  const color = useMemo(
    () =>
      goal
        ? getLinkedItemColor(goal.id, { goals, lifeAreas }) ?? undefined
        : undefined,
    [goal, goals, lifeAreas],
  );

  if (!goal || !startDate) {
    return null;
  }

  const _onClick = () => onClick?.(goal.id);

  return (
    <>
      <EventCard
        ref={containerRef}
        title={goal.name}
        color={color}
        isDeadline={isDeadline}
        isPast={isPast}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        leading={
          <Styled.LeadingSlot $color={color} $past={isPast}>
            <CircleProgress
              percentage={completionPercentage}
              hidePercentage
              isSmall
            />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <GoalContextMenu
          id={goal.id}
          location={menuPosition}
          onClose={closeMenu}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={goal.name}
        delay={tooltipDelay}
      />
    </>
  );
};
